import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Helmet from 'react-helmet'
import { Link, Prompt, useParams, withRouter } from 'react-router-dom'

import Slide from '@material-ui/core/Slide'

import Activity from '../components/Activity'
import CaseNavigationBar from '../components/case/CaseNavigationBar'
import AddButton from '../components/controls/AddButton'
import DatePicker from '../components/controls/DatePicker'
import TextArea from '../components/controls/TextArea'
import CustomDropDown from '../components/CustomDropDown'
import Expense from '../components/Expense'
import FAIcon from '../components/FAIcon'
import NavigationBar from '../components/NavigationBar'
import createFormHelpers from '../FormHelpers'
import AppointmentsService from '../services/AppointmentsService'
import CasesService from '../services/CasesService'
import format from '../services/format'
import GeneralTasksService from '../services/GeneralTasksService'
import setFocusById from '../services/setFocusById'
import TasksService from '../services/TasksService'
import UserService from '../services/UserService'
import { ROLE_NAMES } from '../utils/Constants'
import Permissions, { CAPABILITIES } from '../utils/Permissions'
import validation from '../validation'

import './TaskPage.scss'

// Maps a relatedSection slug to a user-friendly tab label
const sectionLabels = {
  documents: 'Documents',
  overview: 'Overview',
  details: 'Details',
  messages: 'Messages',
}

const emptyTaskModel = (userId, caseId) => {
  const baseModel = {
    complete: false,
    title: '',
    description: '',
    note: '',
    dueDateUtc: new Date(),
    assignedToUserId: userId,
    caseNumber: caseId && caseId !== 'undefined' ? `${caseId}` : null,
  }

  if (caseId) {
    baseModel.activities = []
    baseModel.expenses = []
  }

  if (new URLSearchParams(window.location.search).get('asNote') && caseId) {
    baseModel.activities = [
      {
        date: new Date(),
        caseNumber: `${caseId}`,
        type: '',
        time: '',
        description: '',
        userId: `${userId || ''}`,
      },
    ]
  }

  return baseModel
}

window.counter = 0

const buildDuplicatedTaskModel = (sourceTask, defaultUserId, defaultCaseId) => {
  const resolvedCaseId =
    defaultCaseId && defaultCaseId !== 'undefined'
      ? `${defaultCaseId}`
      : sourceTask?.caseNumber
      ? `${sourceTask.caseNumber}`
      : null

  const base = {
    complete: false,
    title: sourceTask?.title || sourceTask?.Title || '',
    description: sourceTask?.description || sourceTask?.Description || '',
    note: '',
    dueDateUtc: new Date(),
    assignedToUserId: sourceTask?.assignedToUserId || sourceTask?.AssignedToUserId || defaultUserId,
    caseNumber: resolvedCaseId,
    activityType: sourceTask?.activityType || '',
    relatedSection: sourceTask?.relatedSection || '',
    instructions: sourceTask?.instructions || '',
  }

  if (resolvedCaseId) {
    base.activities = base.activityType
      ? [
          {
            date: new Date(),
            type: base.activityType,
            time: '',
            description: base.title,
            userId: defaultUserId,
            caseNumber: resolvedCaseId,
          },
        ]
      : []
    base.expenses = []
  }

  return base
}

const TaskPage = withRouter(({ history, location, caseManager, isInline, refreshCaseStatus }) => {
  /* eslint-disable react-hooks/exhaustive-deps */
  const params = useParams()
  const id = params.id || params.caseNumber
  const { taskId: rawTaskId } = params
  const taskId = rawTaskId === 'new' ? undefined : rawTaskId
  // If "id" (caseId) is missing, it is a General Task
  const isGeneralTask = !id || id === 'undefined'
  const searchParams = new URLSearchParams(location?.search || window.location.search)
  const asNote = searchParams.get('asNote') === 'true'
  const duplicateFromTaskId = searchParams.get('duplicateFrom')
  const taskFormRef = useRef(null)

  const isAdjuster = caseManager?.roles?.includes('claim-rep')

  // existing state
  const [caseModel, setCaseModel] = useState({})
  const [taskModel, setTaskModel] = useState(() =>
    !taskId && location?.state?.duplicateTask
      ? buildDuplicatedTaskModel(location.state.duplicateTask, caseManager.id, id)
      : emptyTaskModel(caseManager.id, id),
  )
  const [cases, setCases] = useState([])
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showRequiredActivitiesError, setShowRequiredActivitiesError] = useState('')
  const [touched, setTouched] = useState({})
  const [caseMembers, setCaseMembers] = useState([])
  const caseMemberOptions = caseMembers.map(cm => ({
    id: cm.id,
    name: cm.role ? `${cm.firstName} ${cm.lastName} (${cm.role})` : `${cm.firstName} ${cm.lastName}`,
  }))
  const [deleteForceWarning, setDeleteForceWarning] = useState('')
  const [showDeleteCompletedConfirm, setShowDeleteCompletedConfirm] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [appointments, setAppointments] = useState([])
  const [selectedAppointmentId, setSelectedAppointmentId] = useState('')
  const [keepNotes, setKeepNotes] = useState(true)
  const [isMerging, setIsMerging] = useState(false)

  // Tasks open directly in edit mode; completed tasks are now always editable.
  const isReadMode = false
  const [continueToNext, setContinueToNext] = useState(false)
  const [originalModel, setOriginalModel] = useState(null)
  const ignorePromptRef = useRef(false)

  useEffect(() => {
    const wasInChain = sessionStorage.getItem('fromContinueToNextChain') === 'true'
    if (wasInChain) {
      setContinueToNext(sessionStorage.getItem('continueToNext') === 'true')
      sessionStorage.removeItem('fromContinueToNextChain')
    } else {
      setContinueToNext(false)
      sessionStorage.removeItem('continueToNext')
    }
  }, [taskId])

  // Inline title editing for completed tasks (#460)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editingTitleValue, setEditingTitleValue] = useState('')

  const handleSaveTitle = () => {
    const trimmed = editingTitleValue.trim()
    if (!trimmed) return
    if (trimmed === getValue('title', '', taskModel)) {
      setIsEditingTitle(false)
      return
    }
    const updated = { ...taskModel, title: trimmed }
    const caseNumber = getValue('caseNumber', '', taskModel)
    setIsLoading(true)
    const savePromise = isGeneralTask
      ? GeneralTasksService.update(updated)
      : TasksService.update(updated, caseNumber, taskId)
    savePromise
      .then(() => {
        setTaskModel(updated)
        setIsEditingTitle(false)
        setError(null)
      })
      .catch(err => setError(err.message || 'Failed to save title'))
      .finally(() => setIsLoading(false))
  }
  const [caseTasks, setCaseTasks] = useState([])

  const {
    createInputProps,
    getValue,
    setModelValue,
    setModelValues,
    handleBlur,
    ErrorMessage,
    addNestedItemToArray,
    removeNestedItemFromArray,
  } = createFormHelpers({
    touchedObject: touched,
    setTouchedFunction: setTouched,
    submitAttempted,
    validationSchema: getValidationSchema(),
  })

  function getValidationSchema() {
    const schema = {
      title: validation.shortText('Title').required(),
      note: validation.note('Note').nullable(),
      assignedToUserId: validation.guid(),
    }
    if (!isGeneralTask && !isAdjuster) {
      schema.caseNumber = validation.shortText('Client').required()
      schema.activities = validation.arrayOf(validation.activity('Activity', !!taskModel.complete))
      schema.expenses = validation.arrayOf(validation.expense('Expense', true))
    } else if (!isGeneralTask && isAdjuster) {
      schema.caseNumber = validation.shortText('Client').required()
    }
    return validation.object(schema)
  }

  useEffect(() => {
    const promises = []

    if (!taskId) {
      if (location?.state?.duplicateTask) {
        setTaskModel(buildDuplicatedTaskModel(location.state.duplicateTask, caseManager.id, id))
      } else {
        setTaskModel(emptyTaskModel(caseManager.id, id))
      }
      setError('')
      setSubmitAttempted(false)
      setTouched({})

      if (duplicateFromTaskId) {
        const loadSourceTask = isGeneralTask
          ? GeneralTasksService.get(duplicateFromTaskId)
          : TasksService.get(id, duplicateFromTaskId)
        promises.push(
          loadSourceTask
            .then(sourceTask => {
              if (sourceTask) {
                setTaskModel(buildDuplicatedTaskModel(sourceTask, caseManager.id, id))
              }
            })
            .catch(() => {
              // Keep state.duplicateTask fallback if fetch fails
            }),
        )
      }
    }

    const casesPromise = CasesService.getAll(['caseNumber', 'client']).then(result => {
      const caseList = setCaseOptions(result)
      setCases(caseList)
    })
    promises.push(casesPromise)

    if (id) {
      const casePromise = CasesService.get(id).then(caseObj => {
        setCaseModel(caseObj)
        const members = caseObj.caseMembers || []
        setCaseMembers(members)

        if (!taskId) {
          setTaskModel(prev => {
            const userIsMember = members.some(m => m.id === prev.assignedToUserId)
            if (!userIsMember) {
              return { ...prev, assignedToUserId: null }
            }
            return prev
          })
        }
      })
      promises.push(casePromise)
    }

    // For General Tasks, load all users to populate the "Assigned To" dropdown
    if (isGeneralTask) {
      const usersPromise = UserService.getAll().then(users => {
        const mappedUsers = users.map(u => {
          const roleLabel =
            u.roles && u.roles.length > 0 ? ROLE_NAMES.find(r => r.key === u.roles[0])?.label || u.roles[0] : ''
          return {
            ...u,
            id: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
            role: roleLabel,
          }
        })
        setCaseMembers(mappedUsers)
      })
      promises.push(usersPromise)
    }

    if (taskId) {
      const loadTask = isGeneralTask ? GeneralTasksService.get(taskId) : TasksService.get(id, taskId)

      const tasksPromise = loadTask.then(r => {
        if (
          r &&
          (!r.note || !r.note.trim()) &&
          r.description &&
          r.description.trim() &&
          r.description.trim().toLowerCase() !== (r.title || '').trim().toLowerCase()
        ) {
          r.note = r.description.trim()
        }
        if (!isGeneralTask && asNote && (!r.activities || r.activities.length === 0)) {
          r.activities = [{ date: new Date(), userId: caseManager.id }]
        }

        // Pre-seed a default activity from the template when first opening an incomplete task
        if (!isGeneralTask && !r.complete && r.activityType && (!r.activities || r.activities.length === 0)) {
          r.activities = [
            {
              date: new Date(),
              type: r.activityType,
              time: '',
              description: r.title,
              userId: caseManager.id,
              caseNumber: id,
            },
          ]
        }

        setTaskModel(r)
      })
      promises.push(tasksPromise)

      // Load sibling tasks for progress indicator + "continue to next" navigation
      if (!isGeneralTask) {
        promises.push(
          TasksService.getAllForCase(id)
            .then(tasks => setCaseTasks(Array.isArray(tasks) ? tasks : []))
            .catch(() => {}),
        )
      }
    } else {
      setIsLoading(false)
    }

    Promise.all(promises)
      .then(() => {
        setTaskModel(prev => {
          setOriginalModel(JSON.parse(JSON.stringify(prev)))
          return prev
        })
        setIsLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setIsLoading(false)
      })
  }, [id, taskId, caseManager.id, isGeneralTask, asNote, duplicateFromTaskId])

  const handleDuplicateTask = () => {
    ignorePromptRef.current = true
    const fromPath = location.state?.from || (isGeneralTask ? '/tasks' : `/cases/${id}`)
    history.push({
      pathname: isGeneralTask ? '/tasks/new' : `/cases/${id}/tasks`,
      search: `?duplicateFrom=${taskId}`,
      state: {
        duplicateTask: taskModel,
        from: fromPath,
      },
    })
  }

  const handleClickExit = () => {
    const fromPath = location.state?.from
    if (fromPath) {
      history.push(fromPath)
    } else if (isGeneralTask) {
      history.push('/tasks')
    } else {
      history.push(id ? `/cases/${id}` : '/tasks')
    }
  }

  const handleClickSave = e => {
    if (e && e.preventDefault) e.preventDefault()
    ignorePromptRef.current = true
    setSubmitAttempted(true)
    const caseNumber = getValue('caseNumber', '', taskModel)

    // Build the task to save: filter out completely blank placeholder rows.
    const updatedTask = { ...taskModel }
    if (!isGeneralTask && updatedTask.activities) {
      updatedTask.activities = updatedTask.activities.filter(a => a.type || a.time || a.description)
    }

    // Validate the filtered model
    const validationResult = validation.validate(updatedTask, getValidationSchema())
    const isValid = !!validationResult && validationResult.success

    if ((isGeneralTask || !!caseNumber) && isValid) {
      // Retaining the "Work Mode" auto-complete for backward compatibility
      // only if asNote is true AND activities exist.
      if (asNote && getValue('activities', [], taskModel).length >= 1) {
        updatedTask.complete = true
      } else {
        updatedTask.complete = !!taskModel.complete
        setShowRequiredActivitiesError(false)
      }

      setIsLoading(true)
      ;(() => {
        if (isGeneralTask) {
          if (taskId) {
            return GeneralTasksService.update(updatedTask)
          } else {
            return GeneralTasksService.create(updatedTask)
          }
        } else {
          if (taskId) {
            return TasksService.update(updatedTask, caseNumber, taskId)
          } else {
            return TasksService.create(updatedTask, caseNumber)
          }
        }
      })()
        .then(() => {
          if (refreshCaseStatus) {
            refreshCaseStatus()
          }
          setError(null)
          // On success: stay on page if still in progress; navigate away if completing
          if (taskId && !updatedTask.complete) {
            setIsLoading(false)
            if (asNote) {
              history.replace(window.location.pathname) // clear asNote param
            }
          } else {
            handleClickExit()
            setIsLoading(false)
          }
        })
        .catch(err => {
          ignorePromptRef.current = false
          setModelValue('complete', false, taskModel, setTaskModel)
          setError(err.message || 'An unexpected error occurred while trying to save the task.')
          setIsLoading(false)
        })
    } else {
      setIsLoading(false)
      setError('Please fix the validation errors above before saving.')
    }
  }

  const handleClickComplete = () => {
    ignorePromptRef.current = true
    // Skip activity validation for adjusters and general tasks (they have no activities section)
    if (!isGeneralTask && !isAdjuster) {
      const activities = getValue('activities', [], taskModel)
      // If there ARE activities, all of them must have time entered
      const allHaveTime = activities.every(a => a.time && parseFloat(a.time) > 0)
      if (activities.length > 0 && !allHaveTime) {
        setError('All activities must have time entered before completing this task.')
        return
      }
    }

    setIsLoading(true)
    const updatedTask = { ...taskModel, complete: true }
    const caseNumber = getValue('caseNumber', '', taskModel)

    const promise = (() => {
      if (isGeneralTask) {
        if (taskId) {
          return GeneralTasksService.update(updatedTask)
        } else {
          return GeneralTasksService.create(updatedTask)
        }
      } else {
        if (taskId) {
          return TasksService.update(updatedTask, caseNumber, taskId)
        } else {
          return TasksService.create(updatedTask, caseNumber)
        }
      }
    })()

    promise
      .then(() => {
        if (refreshCaseStatus) {
          refreshCaseStatus()
        }
        // Navigate to next incomplete task if checkbox is checked
        if (continueToNext && caseTasks.length > 0) {
          const currentIdx = caseTasks.findIndex(t => `${t.taskNumber}` === `${taskId}`)
          const next = caseTasks.slice(currentIdx + 1).find(t => !t.complete)
          if (next) {
            // Use window.location.replace (not history.replace) so the browser performs a full
            // page reload. history.replace keeps the same React component instance alive, which
            // leaves isLoading stuck at true (it was set above and never reset on this code path)
            // and skips the useEffect re-run — causing the blank / header-only screen.
            sessionStorage.setItem('continueToNext', 'true')
            sessionStorage.setItem('fromContinueToNextChain', 'true')
            window.location.replace(`/cases/${id}/tasks/${next.taskNumber}`)
            return
          }
        }
        if (history.length <= 1) {
          history.push(id ? `/cases/${id}` : '/tasks')
        } else {
          history.goBack()
        }
      })
      .catch(err => {
        ignorePromptRef.current = false
        setError(err.message || 'Failed to complete task')
        setIsLoading(false)
      })
  }

  const handleDeleteTask = async (force = false) => {
    setError('')
    ignorePromptRef.current = true
    try {
      await TasksService.deleteTask(id, taskId, force)
      if (refreshCaseStatus) {
        refreshCaseStatus()
      }
      const fromPath = location.state?.from
      if (fromPath) {
        history.push(fromPath)
      } else {
        history.push(id ? `/cases/${id}` : '/tasks')
      }
    } catch (err) {
      ignorePromptRef.current = false
      if (err.requiresForce) {
        setDeleteForceWarning(err.serverMessage)
      } else {
        setError(err.message || 'Failed to delete task')
      }
    }
  }

  const handleOpenMergeModal = async () => {
    setIsLoading(true)
    setError('')
    const caseNumber = id || taskModel.caseNumber
    if (!caseNumber) {
      setError('Cannot merge a task that is not associated with a case')
      setIsLoading(false)
      return
    }
    try {
      const appts = await AppointmentsService.getCaseAppointments(caseNumber)
      setAppointments(appts || [])
      if (appts && appts.length > 0) {
        setSelectedAppointmentId(appts[0].appointmentNumber)
      }
      setShowMergeModal(true)
    } catch (err) {
      setError(err.message || 'Failed to load appointments')
    } finally {
      setIsLoading(false)
    }
  }

  const handleExecuteMerge = async () => {
    if (!selectedAppointmentId) return
    setIsMerging(true)
    ignorePromptRef.current = true
    const caseNumber = id || taskModel.caseNumber
    try {
      await TasksService.mergeToAppointment(caseNumber, taskId, selectedAppointmentId, keepNotes)
      setShowMergeModal(false)
      if (refreshCaseStatus) {
        refreshCaseStatus()
      }
      const fromPath = location.state?.from
      if (fromPath) {
        history.push(fromPath)
      } else {
        history.push(caseNumber ? `/cases/${caseNumber}` : '/tasks')
      }
    } catch (err) {
      ignorePromptRef.current = false
      setError(err.message || 'Failed to merge task')
    } finally {
      setIsMerging(false)
    }
  }

  const handleClientSelectChange = e => {
    setModelValue(e.target.name, e.target.value, taskModel, setTaskModel)
  }

  const expenseOnChangeHandler = (e, namePrefix) => {
    const modelPaths = [{ name: e.target.name, value: e.target.value }]

    if (e.target.name.includes('type') && e.target.value === 'Mileage') {
      modelPaths.push({ name: `${namePrefix}.amount`, value: 0 })
    } else if (e.target.name.includes('type')) {
      modelPaths.push({ name: `${namePrefix}.distance`, value: 0 })
    }

    setModelValues(modelPaths, taskModel, setTaskModel)
  }

  const handleAddExpense = () => {
    const expenses = getValue('expenses', [], taskModel)
    const nextIndex = expenses.length

    setFocusById(`expenses[${nextIndex}].type`)
    addNestedItemToArray(
      'expenses',
      { date: new Date(), type: '', amount: 0, distance: 0, description: '', userId: caseManager.id },
      taskModel,
      setTaskModel,
    )
  }

  const handleAddActivity = () => {
    const activities = getValue('activities', [], taskModel)
    const nextIndex = activities.length

    setFocusById(`activities[${nextIndex}].type`)
    addNestedItemToArray(
      'activities',
      { date: new Date(), type: '', time: '', description: '', userId: caseManager.id },
      taskModel,
      setTaskModel,
    )
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
  }

  const handleDrop = (e, targetIndex) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === targetIndex) return
    handleReorder(draggedIndex, targetIndex)
    setDraggedIndex(null)
  }

  const handleReorder = (fromIndex, toIndex) => {
    const activities = [...getValue('activities', [], taskModel)]
    const act1 = activities[fromIndex]
    const act2 = activities[toIndex]
    if (!act1 || !act2) return

    const date1 = act1.date ? new Date(act1.date).toDateString() : ''
    const date2 = act2.date ? new Date(act2.date).toDateString() : ''
    if (date1 !== date2) return

    activities[fromIndex] = act2
    activities[toIndex] = act1

    const groups = {}
    activities.forEach((act, idx) => {
      const dStr = act.date ? new Date(act.date).toDateString() : ''
      if (!groups[dStr]) groups[dStr] = []
      groups[dStr].push({ act, idx })
    })

    Object.keys(groups).forEach(dStr => {
      groups[dStr].forEach((item, order) => {
        activities[item.idx] = {
          ...item.act,
          sortOrder: order,
        }
      })
    })

    setTaskModel({ ...taskModel, activities })
  }

  function setCaseOptions(caseList) {
    const parsedCases = caseList
      ? caseList.map(caseObj => ({
          id: getValue('caseNumber', '', caseObj),
          name: `${getValue('caseNumber', '', caseObj)} - ${getValue('client.firstName', '', caseObj)} ${getValue(
            'client.lastName',
            '',
            caseObj,
          )}`,
        }))
      : []

    setCases(parsedCases)
    if (!taskId) {
      setFocusById('title')
    }

    return parsedCases
  }

  // Pre-compute for progress indicator and "continue to next" navigation
  const taskIndex = !isGeneralTask && taskId ? caseTasks.findIndex(t => `${t.taskNumber}` === `${taskId}`) : -1
  const completedCount = caseTasks.filter(t => t.complete).length

  return (
    <Slide className="container-case-page background" direction="up" in={true} mountOnEnter unmountOnExit>
      <div>
        <form ref={taskFormRef} className="new-task-container" onSubmit={handleClickSave}>
          <Helmet
            title={taskId ? `Task - Case ${id || 'General'} - ${getValue('title', '', taskModel)}` : 'New Task'}
          />
          <Prompt
            when={
              !ignorePromptRef.current &&
              originalModel !== null &&
              JSON.stringify(taskModel) !== JSON.stringify(originalModel)
            }
            message="You have unsaved changes. Are you sure you want to leave?"
          />
          {isInline ? (
            <div
              className="d-flex justify-content-between align-items-center mt-3 mb-3"
              style={{
                paddingLeft: '15px',
                paddingRight: '15px',
              }}
            >
              <h4 className="text-primary font-weight-bold mb-0">
                {taskModel?.complete ? 'Completed Task' : 'Open Task'}
              </h4>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClickExit}>
                Cancel
              </button>
            </div>
          ) : taskId !== undefined ? (
            <CaseNavigationBar
              title={getValue('title', '', taskModel)}
              subtitle={
                !isGeneralTask && id
                  ? `${getValue('client.firstName', '', caseModel)} ${getValue(
                      'client.lastName',
                      '',
                      caseModel,
                    )} \u00b7 CASE #: ${id}`
                  : undefined
              }
              handleClickLeft={handleClickExit}
              shadow="shadow"
              caseNumber={!isGeneralTask && id ? id : undefined}
              isInline="sticky"
            />
          ) : !isGeneralTask && id ? (
            // New Task under a Case Context
            <CaseNavigationBar
              title={`${getValue('client.firstName', '', caseModel).toUpperCase()} ${getValue(
                'client.lastName',
                '',
                caseModel,
              ).toUpperCase()}`}
              subtitle="NEW TASK"
              rightButtonText="SAVE"
              handleClickLeft={handleClickExit}
              handleRightClick={handleClickSave}
              shadow="shadow"
              caseNumber={id}
              isInline="sticky"
            />
          ) : (
            // Global New Task
            <NavigationBar
              title="NEW TASK"
              rightBtnText="SAVE"
              leftBtnText="Cancel"
              handleClickLeft={handleClickExit}
              handleClickRight={handleClickSave}
              isInline="sticky"
            />
          )}
          {!isLoading ? (
            <React.Fragment>
              {!!deleteForceWarning && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Delete Task</h5>
                      </div>
                      <div className="modal-body">
                        {taskModel.complete ? (
                          <>
                            <p className="text-danger">
                              This task has been completed and included in a Progress Report. Deleting it will remove it
                              from the report.
                            </p>
                            <p>Are you sure you want to permanently delete this task and all associated data?</p>
                          </>
                        ) : (
                          // Incomplete task on a report — existing force-delete path
                          <>
                            <p>{deleteForceWarning}</p>
                            <p>Are you sure you want to permanently delete this task and all associated data?</p>
                          </>
                        )}
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setDeleteForceWarning('')}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => {
                            setDeleteForceWarning('')
                            handleDeleteTask(true)
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {showDeleteCompletedConfirm && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Delete Completed Task</h5>
                      </div>
                      <div className="modal-body">
                        <p>
                          This task has been marked complete. Are you sure you want to permanently delete it and all
                          associated data?
                        </p>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowDeleteCompletedConfirm(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => {
                            setShowDeleteCompletedConfirm(false)
                            handleDeleteTask(false)
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="new-task-container">
                {!!error && (
                  <Alert variant="danger" className="mb-3 pr-5 pl-5">
                    <pre className="mb-0">{error}</pre>
                  </Alert>
                )}
                <div className="container">
                  <div>
                    {taskId ? (
                      <React.Fragment>
                        <div className="d-flex justify-content-end mb-2">
                          {(isGeneralTask || Permissions.can(caseManager, CAPABILITIES.CREATE_TASK)) && (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm mr-2"
                              onClick={handleDuplicateTask}
                              title="Duplicate this task"
                            >
                              <FAIcon name="copy" className="mr-1" /> Duplicate Task
                            </button>
                          )}
                          {!isGeneralTask && Permissions.can(caseManager, CAPABILITIES.DELETE_TASK) && (
                            <OverlayTrigger
                              placement="left"
                              overlay={
                                <Tooltip>
                                  {taskModel.complete ? 'Delete this completed task' : 'Delete this task'}
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() =>
                                  taskModel.complete ? setShowDeleteCompletedConfirm(true) : handleDeleteTask(false)
                                }
                              >
                                <FAIcon name="trash-alt" type="far" className="mr-1" /> Delete Task
                              </button>
                            </OverlayTrigger>
                          )}
                        </div>
                        {isReadMode ? (
                          isEditingTitle ? (
                            <div className="d-flex align-items-center mb-3" style={{ gap: '8px' }}>
                              <input
                                id="edit-title-input"
                                className="form-control"
                                value={editingTitleValue}
                                onChange={e => setEditingTitleValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSaveTitle()
                                  if (e.key === 'Escape') setIsEditingTitle(false)
                                }}
                              />
                              <button type="button" className="btn btn-sm btn-primary" onClick={handleSaveTitle}>
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setIsEditingTitle(false)}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center pb-3">
                              <h4 className="header-title mb-0 mr-2">{getValue('title', '', taskModel)}</h4>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                onClick={() => {
                                  setEditingTitleValue(getValue('title', '', taskModel))
                                  setIsEditingTitle(true)
                                  setFocusById('edit-title-input')
                                }}
                                title="Edit task title"
                              >
                                <FAIcon name="pen" />
                              </button>
                            </div>
                          )
                        ) : (
                          <div className="form-row">
                            <div className="col-md-12">
                              <div className="form-group">
                                <label className="form-label" htmlFor="title">
                                  Title
                                </label>
                                <input
                                  className="form-control mb-3"
                                  id="title"
                                  {...createInputProps({
                                    name: 'title',
                                    model: taskModel,
                                    setModelFunction: setTaskModel,
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Task template instructions — read-only rich text, shown in both read and edit mode */}
                        {taskModel.instructions && (
                          <div className="task-instructions-block mt-2 mb-3">
                            <div
                              className="task-instructions-content"
                              dangerouslySetInnerHTML={{ __html: taskModel.instructions }}
                            />
                            <hr className="subsection-divider mt-2 mb-0" />
                          </div>
                        )}

                        {/* Contextual deep link to related case tab (template-driven tasks only) */}
                        {taskModel.relatedSection && !taskModel.complete && (
                          <div className="task-related-link mt-1 mb-3">
                            <Link
                              to={`/cases/${id}/${taskModel.relatedSection}`}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              <FAIcon name="external-link-alt" className="mr-1" />
                              Go to {sectionLabels[taskModel.relatedSection] || taskModel.relatedSection} tab
                            </Link>
                            <small className="ml-2 text-muted">Use the back arrow ← to return to this task.</small>
                          </div>
                        )}

                        {taskIndex >= 0 && (
                          <div className="task-progress text-muted small mb-2">
                            Task {taskIndex + 1} of {caseTasks.length}
                            <span className="ml-2 text-success">&#10003; {completedCount} completed</span>
                          </div>
                        )}

                        <div id="dateCreated" className="date-created">
                          Created on {format.shortDateTime(getValue('createdUtc', '', taskModel))}
                        </div>

                        {isReadMode ? (
                          <div>
                            <div>Due on {format.date(getValue('dueDateUtc', '', taskModel), false)}</div>
                            {getValue('assignedToUserId', '', taskModel) && (
                              <div className="mt-1 text-muted small">
                                Assigned to:{' '}
                                {getValue('assignedToUserName', null, taskModel) ||
                                  `${
                                    caseMembers.find(x => x.id === getValue('assignedToUserId', '', taskModel))
                                      ?.firstName || ''
                                  } ${
                                    caseMembers.find(x => x.id === getValue('assignedToUserId', '', taskModel))
                                      ?.lastName || ''
                                  }`}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="form-row">
                            <div className="col-md-4">
                              <div className="form-group mt-2">
                                <label className="form-label">Due Date</label>
                                <DatePicker
                                  {...createInputProps({
                                    name: 'dueDateUtc',
                                    model: taskModel,
                                    setModelFunction: setTaskModel,
                                  })}
                                  placeholderText="Due Date"
                                />
                              </div>
                            </div>
                            <div className="col-md-8">
                              <div className="form-group mt-2">
                                <label className="form-label">Assigned To</label>
                                <CustomDropDown
                                  id="assignedToUserId"
                                  name="assignedToUserId"
                                  value={getValue('assignedToUserId', '', taskModel)}
                                  items={caseMembers.map(m => ({
                                    id: m.id,
                                    name: `${m.firstName} ${m.lastName}${
                                      m.role && m.role.trim().length > 0 ? ` (${m.role})` : ''
                                    }`,
                                  }))}
                                  onChange={e =>
                                    setModelValue('assignedToUserId', e.target.value, taskModel, setTaskModel)
                                  }
                                  placeholderText="Unassigned"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ) : null}
                  </div>
                  {taskId ? null : (
                    <div className="form-row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label mt-2" htmlFor="title">
                            Title
                          </label>
                          <input
                            className="form-control"
                            id="title"
                            {...createInputProps({ name: 'title', model: taskModel, setModelFunction: setTaskModel })}
                          />
                          <ErrorMessage name="title" model={taskModel} />
                          <label className="form-label mt-2" htmlFor="title">
                            Due Date
                          </label>
                          <DatePicker
                            {...createInputProps({
                              name: 'dueDateUtc',
                              model: taskModel,
                              setModelFunction: setTaskModel,
                            })}
                          />
                          <ErrorMessage name="due-date" model={taskModel} />

                          {/* Assigned To Dropdown */}
                          <label className="form-label mt-2" htmlFor="assignedToUserId">
                            Assigned To
                          </label>
                          <CustomDropDown
                            id="assignedToUserId"
                            name="assignedToUserId"
                            value={getValue('assignedToUserId', '', taskModel)}
                            items={caseMembers.map(m => ({
                              id: m.id,
                              name: `${m.firstName} ${m.lastName} (${m.role})`,
                            }))}
                            onChange={e => setModelValue('assignedToUserId', e.target.value, taskModel, setTaskModel)}
                            placeholderText="Unassigned"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Client Dropdown Logic - Only for Global New Tasks */}
                  {!isGeneralTask && (taskId === undefined || taskId === '') && !id ? (
                    <div className="form-row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <label className="form-label mt-2" htmlFor="caseNumber">
                            Client
                          </label>
                          <CustomDropDown
                            id="caseNumber"
                            name="caseNumber"
                            value={id || getValue('caseNumber', null, taskModel)}
                            isDisabled={!!id}
                            className="drop-down-container"
                            items={cases}
                            placeholderText="Search Clients"
                            noOptionsMessage="No results found"
                            onChange={handleClientSelectChange}
                            onBlur={handleBlur}
                          />
                          <ErrorMessage name="caseNumber" model={taskModel} />
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {!isGeneralTask && !isAdjuster && (
                    <React.Fragment>
                      <hr className="subsection-divider" />
                      <div>
                        <h5 className="pb-1 header-title">Time</h5>
                      </div>
                      {isReadMode ? (
                        getValue('activities', [], taskModel).length > 0 ? (
                          <table
                            className="table table-sm table-striped task-details-table"
                            style={{ tableLayout: 'fixed' }}
                          >
                            <thead>
                              <tr>
                                <th className="text-nowrap" style={{ width: '15%' }}>
                                  Date
                                </th>
                                <th className="text-nowrap" style={{ width: '22%' }}>
                                  Type
                                </th>
                                <th className="text-nowrap" style={{ width: '8%' }}>
                                  Time
                                </th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getValue('activities', [], taskModel).map((a, index) => (
                                <tr key={index}>
                                  <td className="text-nowrap">{format.date(a.date)}</td>
                                  <td className="text-nowrap">{a.type}</td>
                                  <td className="text-nowrap">{a.time} hrs.</td>
                                  <td>{a.description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="text-muted font-italic mb-3">No activities recorded.</div>
                        )
                      ) : (
                        <React.Fragment>
                          {getValue('activities', [], taskModel).length > 0 && (
                            <div
                              className="form-row hide-sm-down"
                              style={{ paddingLeft: '11px', paddingRight: '11px' }}
                            >
                              <div className="form-label col-md-2">Date</div>
                              <div className="form-label col-md-2">Type of Activity</div>
                              <div className="form-label col-md-1">Time</div>
                              <div className="form-label col-md-4">Description</div>
                              <div className="form-label col-md-2">Case Member</div>
                              <div className="form-label col-md-1"></div>
                            </div>
                          )}
                          <div className="position-relative">
                            {(() => {
                              const activities = getValue('activities', [], taskModel)
                              return activities.map((x, index) => {
                                const namePrefix = `activities[${index}]`
                                const showMoveUp =
                                  index > 0 &&
                                  (x.date ? new Date(x.date).toDateString() : '') ===
                                    (activities[index - 1].date
                                      ? new Date(activities[index - 1].date).toDateString()
                                      : '')
                                const showMoveDown =
                                  index < activities.length - 1 &&
                                  (x.date ? new Date(x.date).toDateString() : '') ===
                                    (activities[index + 1].date
                                      ? new Date(activities[index + 1].date).toDateString()
                                      : '')

                                return (
                                  <Activity
                                    namePrefix={namePrefix}
                                    key={index}
                                    index={index}
                                    model={taskModel}
                                    setModelFunction={setTaskModel}
                                    createInputProps={createInputProps}
                                    ErrorMessage={ErrorMessage}
                                    handleRemove={() =>
                                      removeNestedItemFromArray('activities', index, taskModel, setTaskModel)
                                    }
                                    allowEditing={!isReadMode}
                                    onMoveUp={showMoveUp ? () => handleReorder(index, index - 1) : null}
                                    onMoveDown={showMoveDown ? () => handleReorder(index, index + 1) : null}
                                    onDragStart={e => handleDragStart(e, index)}
                                    onDragOver={e => handleDragOver(e, index)}
                                    onDrop={e => handleDrop(e, index)}
                                    onDragEnd={() => setDraggedIndex(null)}
                                    caseMemberOptions={caseMemberOptions}
                                  />
                                )
                              })
                            })()}
                            <AddButton
                              id="addActivityButton"
                              className={getValue('activities', [], taskModel).length > 0 ? 'inline-add-button' : ''}
                              onClick={handleAddActivity}
                            >
                              Add Activity
                            </AddButton>
                            {!!showRequiredActivitiesError && (
                              <div className={'text-danger form-text'}>At least one activity must be added</div>
                            )}
                          </div>
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  )}

                  <hr className="subsection-divider" />
                  <div className="form-row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label className="form-label" htmlFor="note">
                          <h5 className="header-title">Notes</h5>
                        </label>
                        {isReadMode ? (
                          <div className="p-2" dangerouslySetInnerHTML={{ __html: getValue('note', '', taskModel) }} />
                        ) : (
                          <TextArea
                            areaProps={{
                              ...createInputProps({ name: 'note', model: taskModel, setModelFunction: setTaskModel }),
                            }}
                            rows="3"
                          />
                        )}

                        <ErrorMessage name={'note'} model={taskModel} />
                      </div>
                    </div>
                  </div>

                  {!isGeneralTask && !isAdjuster && (
                    <React.Fragment>
                      <hr className="subsection-divider" />
                      <div>
                        <h5 className="pb-1 header-title">Expenses</h5>
                      </div>
                      {isReadMode ? (
                        getValue('expenses', [], taskModel).length > 0 ? (
                          <table
                            className="table table-sm table-striped task-details-table"
                            style={{ tableLayout: 'fixed' }}
                          >
                            <thead>
                              <tr>
                                <th className="text-nowrap" style={{ width: '10%' }}>
                                  Date
                                </th>
                                <th className="text-nowrap" style={{ width: '15%' }}>
                                  Type
                                </th>
                                <th className="text-nowrap" style={{ width: '10%' }}>
                                  Amt
                                </th>
                                <th className="text-nowrap" style={{ width: '10%' }}>
                                  Dist
                                </th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getValue('expenses', [], taskModel).map((e, index) => (
                                <tr key={index}>
                                  <td className="text-nowrap">{format.date(e.date)}</td>
                                  <td className="text-nowrap">{e.type}</td>
                                  <td className="text-nowrap">{e.amount ? `$ ${e.amount}` : '-'}</td>
                                  <td className="text-nowrap">{e.distance ? `${e.distance} mi` : '-'}</td>
                                  <td>{e.description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="text-muted font-italic mb-3">No expenses recorded.</div>
                        )
                      ) : (
                        <React.Fragment>
                          {getValue('expenses', [{}], taskModel).length > 0 && (
                            <div className="form-row hide-sm-down">
                              <div className="form-label col-md-2">Date</div>
                              <div className="form-label col-md-2">Type of Expense</div>
                              <div className="form-label col-md-1">Amount</div>
                              <div className="form-label col-md-1">Distance</div>
                              <div className="form-label col-md-3">Description</div>
                              <div className="form-label col-md-2">Case Member</div>
                              <div className="form-label col-md-1"></div>
                            </div>
                          )}
                          <div className="position-relative">
                            {getValue('expenses', [], taskModel).map((x, index) => {
                              const namePrefix = `expenses[${index}]`
                              const expenseIsMileage = getValue(`expenses[${index}].type`, '', taskModel) === 'Mileage'
                              return (
                                <Expense
                                  namePrefix={namePrefix}
                                  key={index}
                                  createInputProps={createInputProps}
                                  onChangeHandler={expenseOnChangeHandler}
                                  model={taskModel}
                                  setModelFunction={setTaskModel}
                                  ErrorMessage={ErrorMessage}
                                  getValue={getValue}
                                  expenseIsMileage={expenseIsMileage}
                                  handleRemove={() =>
                                    removeNestedItemFromArray('expenses', index, taskModel, setTaskModel)
                                  }
                                  allowEditing={!isReadMode}
                                  caseMemberOptions={caseMemberOptions}
                                />
                              )
                            })}
                            <AddButton
                              id="addExpenseButton"
                              className={getValue('expenses', [{}], taskModel).length > 0 ? 'inline-add-button' : ''}
                              onClick={handleAddExpense}
                            >
                              Add Expense
                            </AddButton>
                          </div>
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  )}

                  {(taskId || isInline) && (
                    <div className="d-flex flex-column align-items-center mt-4 mb-4">
                      <div className="d-flex align-items-center">
                        {/* Save — always active; incomplete activities are filtered before persisting */}
                        <Button
                          variant="outline-primary"
                          size="lg"
                          className={taskModel.complete ? '' : 'mr-3'}
                          onClick={handleClickSave}
                        >
                          <FAIcon name="save" type="far" className="mr-2" />
                          SAVE
                        </Button>

                        {/* Complete Task — only if not already complete */}
                        {!taskModel.complete &&
                          (!isGeneralTask &&
                          !isAdjuster &&
                          (() => {
                            const acts = getValue('activities', [], taskModel)
                            return acts.length > 0 && acts.some(a => !a.time || parseFloat(a.time) <= 0)
                          })() ? (
                            <OverlayTrigger
                              placement="top"
                              trigger={['hover', 'focus']}
                              overlay={<Tooltip>Add time to all activities to complete this task.</Tooltip>}
                            >
                              <span className="d-inline-block">
                                <Button variant="success" size="lg" disabled style={{ pointerEvents: 'none' }}>
                                  <FAIcon name="check-circle" className="mr-2" />
                                  COMPLETE TASK
                                </Button>
                              </span>
                            </OverlayTrigger>
                          ) : (
                            <Button variant="success" size="lg" onClick={handleClickComplete}>
                              <FAIcon name="check-circle" className="mr-2" />
                              COMPLETE TASK
                            </Button>
                          ))}

                        {/* Merge to Appointment */}
                        {(id || taskModel.caseNumber) && taskId && (
                          <Button
                            variant="outline-secondary"
                            size="lg"
                            className="ml-3"
                            onClick={handleOpenMergeModal}
                            title="Merge this task into an appointment"
                          >
                            <FAIcon name="compress-alt" className="mr-2" />
                            MERGE
                          </Button>
                        )}
                      </div>

                      {!taskModel.complete && !isGeneralTask && caseTasks.filter(t => !t.complete).length > 1 && (
                        <div className="form-check mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="continueToNextCheck"
                            checked={continueToNext}
                            onChange={e => {
                              setContinueToNext(e.target.checked)
                              sessionStorage.setItem('continueToNext', e.target.checked ? 'true' : 'false')
                            }}
                          />
                          <label className="form-check-label text-muted small" htmlFor="continueToNextCheck">
                            Continue to next task upon completion
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          ) : (
            <div className="d-flex justify-content-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}
        </form>

        <Modal size="lg" show={showMergeModal} onHide={() => setShowMergeModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Merge Task to Appointment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-danger font-weight-bold">
              Warning: Merging this task into an appointment will move all of its activities and expenses to the
              selected appointment. You will lose the task title, notes, instructions, assignee, and status. The parent
              task will be permanently deleted.
            </p>
            {appointments.length === 0 ? (
              <div className="alert alert-warning">
                No appointments found in this case. You must create an appointment first.
              </div>
            ) : (
              <Form.Group controlId="mergeTargetAppointment">
                <Form.Label>Select Target Appointment</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedAppointmentId}
                  onChange={e => setSelectedAppointmentId(e.target.value)}
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    paddingRight: '35px',
                  }}
                >
                  {appointments.map(appt => {
                    const dateStr = format.date(appt.startUtc)
                    const parts = []
                    if (appt.title) parts.push(appt.title)
                    if (appt.individualProvider) parts.push(appt.individualProvider)
                    if (appt.provider) parts.push(appt.provider)
                    const textStr = parts.join(' / ')
                    const displayName = textStr
                      ? `${dateStr} - ${textStr}`
                      : `${dateStr} - Appointment #${appt.appointmentNumber}`
                    return (
                      <option key={appt.appointmentNumber} value={appt.appointmentNumber}>
                        {displayName}
                      </option>
                    )
                  })}
                </Form.Control>
              </Form.Group>
            )}
            <Form.Group controlId="keepNotesCheckbox" className="mt-3">
              <Form.Check
                type="checkbox"
                label="Keep task details (title, notes, description) as notes on the selected appointment."
                checked={keepNotes}
                onChange={e => setKeepNotes(e.target.checked)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMergeModal(false)}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleExecuteMerge} disabled={isMerging || appointments.length === 0}>
              {isMerging ? 'Merging...' : 'Confirm Merge'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Slide>
  )
})

export default TaskPage
